import { useMemo } from 'react';
import {
  ConfigurationRuleScopeEntityType,
  DepartmentResponse,
  RegionResponse,
  StoreResponse,
  StoreTypeResponse,
} from '@ydistri/api-sdk';
import {
  ListType,
  useGetDepartmentsQuery,
  useGetRegionsQuery,
  useGetStoresQuery,
  useGetStoreTypesQuery,
} from '../../../apis/apiLists';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';

interface EntityListData<T> {
  data: ListType<T> | undefined;
  loading: boolean;
}

export interface EntityTypeToResponseMap {
  [ConfigurationRuleScopeEntityType.Regions]: RegionResponse;
  [ConfigurationRuleScopeEntityType.Stores]: StoreResponse;
  [ConfigurationRuleScopeEntityType.StoreTypes]: StoreTypeResponse;
  [ConfigurationRuleScopeEntityType.Departments]: DepartmentResponse;
}

export function useEntityListData<T extends keyof EntityTypeToResponseMap>(
  entityType: T,
): EntityListData<EntityTypeToResponseMap[T]> {
  const templateOrCalculation = useTemplateOrCalculationWithValue();

  const entityQueries = {
    [ConfigurationRuleScopeEntityType.Regions]: useGetRegionsQuery(templateOrCalculation.value),
    [ConfigurationRuleScopeEntityType.Stores]: useGetStoresQuery(templateOrCalculation.value),
    [ConfigurationRuleScopeEntityType.StoreTypes]: useGetStoreTypesQuery(
      templateOrCalculation.value,
    ),
    [ConfigurationRuleScopeEntityType.Departments]: useGetDepartmentsQuery(
      templateOrCalculation.value,
    ),
  };

  const query = entityQueries[entityType];

  return useMemo(
    () => ({
      data: query.data,
      loading: query.isFetching,
    }),
    [query.data, query.isFetching],
  );
}
